import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { PageSection } from "@app/shared";
import { ResultsSummaryTableData, ReviewProjectsTableData } from "@app/shared/provider-change-wizards/interfaces";
import { QESAlert, QESCard, QESCheckbox, QESRadioButton, QESSelect, QESTextArea, QESTextInput } from "@app/shared/qes-standards";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { Roles, UserContext } from "@app/user";
import { ChangeEvent, JSX, useContext, useEffect, useState } from "react";
import { BulkTargetingWizardContext } from "./TargetingWizardContext";
import { BulkTargetExistingTargetActionEnum, BulkTargetingWizardStepProps } from "./interfaces";

const TargetingWizardConfigureReport = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const wizardContext = useContext(BulkTargetingWizardContext);
  const userRoles = useContext(UserContext).roles;
  const [targetingReasons, setTargetingReason] = useState<JSX.Element[]>([]);
  const isDistinctServicingProviderReportEnabled = featureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT) && userRoles.includes(Roles.DISTINCT_SERVICING_USER);

  const reviewColumnConfig: ColumnConfig = {
    details: {
      displayText: "",
      noFilter: true,
      noSort: true,
      testId: "details",
    },
    projectName: {
      displayText: `Projects (${wizardContext.reviewProjects.length})`,
      testId: "project-name",
    },
    validItems: {
      displayText: "Results Summary",
      noFilter: true,
      noSort: true,
      testId: "results-summary",
    },
  };

  useEffect(() => {
    wizardContext.projectionProviderService.getProviderTargetingReasons().then((response) => setTargetingReason(response.map((r, i) => <option key={i} value={r}>{r}</option>)));
  }, []);

  useEffect(() => {
    props.stepIsValid(!!wizardContext.targetingReason && (wizardContext.targetingReason !== "Other" || !!wizardContext.comment) && !!wizardContext.formatType && !!wizardContext.filename && !!wizardContext.existingTargetAction);
  }, [wizardContext.targetingReason, wizardContext.comment, wizardContext.formatType, wizardContext.filename, wizardContext.existingTargetAction]);

  return (
    <PageSection>
      <div className="container">
        <QESAlert type="warning" persist={false} classNames="mb-3">Your analysis will overwrite any existing comments and reasons for existing targeted providers in selected projects.</QESAlert>
        <div className="d-flex mb-3">
          <div className="col-6 pl-0 h-100">
            <QESCard title="Details" classNames="h-100">
              <label className="qes-select__label">
                Action Type
                <span className="text-danger"> *</span>
              </label>
              <div className="pl-3 mb-3">
                <div className="mb-2">
                  <QESRadioButton
                    label="Append to existing targeted providers"
                    name="target-action-type"
                    id="append-action"
                    value={BulkTargetExistingTargetActionEnum.Append}
                    checked={wizardContext.existingTargetAction === BulkTargetExistingTargetActionEnum.Append}
                    onChange={(e) => wizardContext.setExistingTargetAction((e.target as HTMLInputElement).value as BulkTargetExistingTargetActionEnum)}
                    testId="action-targeting__append"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Replace existing targeted providers"
                    name="target-action-type"
                    id="replace-action"
                    value={BulkTargetExistingTargetActionEnum.Replace}
                    checked={wizardContext.existingTargetAction === BulkTargetExistingTargetActionEnum.Replace}
                    onChange={(e) => wizardContext.setExistingTargetAction((e.target as HTMLInputElement).value as BulkTargetExistingTargetActionEnum)}
                    testId="action-targeting__replace"
                  />
                </div>
              </div>
              <QESSelect
                label="Targeting Reason"
                placeholder="Targeting Reason"
                required={true}
                testId="import-targeting__reason"
                value={wizardContext.targetingReason}
                onChange={(e) => wizardContext.setTargetingReason((e.target as HTMLSelectElement).value)}
                isValid={!!wizardContext.targetingReason}
              >
                {targetingReasons}
              </QESSelect>
              <QESTextArea
                label="Comments"
                placeholder="Type here to enter comments..."
                maxLength={100}
                testId="import-targeting__comments"
                required={wizardContext.targetingReason === "Other"}
                value={wizardContext.comment}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => wizardContext.setComment((e.target as HTMLTextAreaElement).value)}
                isValid={!!wizardContext.comment}
              />
            </QESCard>
          </div>
          <div className="col-6 pr-0">
            <QESCard title="Report" classNames="h-100">
              <QESTextInput label="Report Name" testId="import-targeting__export-name" value={wizardContext.filename} onChange={(e: ChangeEvent<HTMLInputElement>) => wizardContext.setFilename(e.target.value)} />
              <p className="mb-2">Report Type</p>
              <div className="pl-3 mb-3">
                <div className="mb-2">
                  <QESRadioButton
                    label="CSV File"
                    name="report-type"
                    id="csv-file"
                    value="CSV"
                    checked={wizardContext.formatType === "CSV"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-csv"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Pipe-delimited Text"
                    name="report-type"
                    id="pipe-delimited-text"
                    value="Pipe"
                    checked={wizardContext.formatType === "Pipe"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-pipe"
                  />
                </div>
                <div className="mb-2">
                  <QESRadioButton
                    label="Tab-delimited Text"
                    name="report-type"
                    id="tab-delimited-text"
                    value="Tab"
                    checked={wizardContext.formatType === "Tab"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-targeting__format-tab"
                  />
                </div>
              </div>
              <div className="pl-2 mt-2 mb-2">
                <QESCheckbox
                  containerClassName="form-check"
                  label="Alert me when the report is ready to view"
                  className="form-check-input"
                  id="alert-when-ready"
                  checked={wizardContext.emailOnSuccess}
                  onChange={(e) => wizardContext.setEmailOnSuccess((e.target as HTMLInputElement).checked)}
                  testId="import-targeting__email-me"
                />
              </div>
              {isDistinctServicingProviderReportEnabled &&
                <div className="pl-2 mt-2 mb-2">
                  <QESCheckbox
                    label="Distinct Servicing Providers"
                    checked={wizardContext.isDistinctServicingProviderReport}
                    onChange={(e) => { wizardContext.setIsDistinctServicingProviderReport(e.target.checked); }}
                    testId="volume-reports__distinct-servicing-provider-report"
                    containerClassName="mb-3"
                  />
                </div>
              }
            </QESCard>
          </div>
        </div>
        <div className="mb-2">
          <Waffles<ResultsSummaryTableData>
            columns={reviewColumnConfig}
            data={wizardContext.resultsSummary}
            fullWidth={true}
            testId="import-targeting-results-summary"
          />
        </div>
        <div className="mb-2">
          <Waffles<ReviewProjectsTableData>
            columns={reviewColumnConfig}
            data={wizardContext.reviewProjects}
            fullWidth={true}
            testId="import-targeting-review-projects"
          />
        </div>
      </div>
    </PageSection>
  );
};

export default TargetingWizardConfigureReport;
